import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

export interface ManualItem {
  description: string;
  quantity: number;
  unitPriceExGst: number;
  notes: string | null;
}

export interface AddManualItemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAdd: (item: ManualItem) => void;
}

export function AddManualItemDialog({ open, onOpenChange, onAdd }: AddManualItemDialogProps) {
  const [description, setDescription] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitPrice, setUnitPrice] = useState("0");
  const [notes, setNotes] = useState("");

  const reset = () => {
    setDescription("");
    setQuantity("1");
    setUnitPrice("0");
    setNotes("");
  };

  const handleAdd = () => {
    if (!description.trim()) {
      toast.error("Description is required");
      return;
    }
    const qty = Number(quantity);
    const price = Number(unitPrice);
    if (!Number.isFinite(qty) || qty <= 0) {
      toast.error("Quantity must be a positive number");
      return;
    }
    if (!Number.isFinite(price) || price < 0) {
      toast.error("Unit price must be zero or more");
      return;
    }
    onAdd({
      description: description.trim(),
      quantity: qty,
      unitPriceExGst: price,
      notes: notes.trim() || null,
    });
    reset();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add item</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label htmlFor="mi-desc">Description</Label>
            <Input
              id="mi-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="mi-qty">Quantity</Label>
            <Input
              id="mi-qty"
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="mi-price">Unit price (ex GST)</Label>
            <Input
              id="mi-price"
              type="number"
              min="0"
              step="any"
              value={unitPrice}
              onChange={(e) => setUnitPrice(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="mi-notes">Notes</Label>
            <Input
              id="mi-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleAdd}>Add item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
